import api, { detectPlatform, getChatList as getChats, updateChat, createChat, getKnowledge, getUserSettings, updateUserSettings } from './api';
import indexedDBService from './indexedDBService';
import type { Chat, Knowledge } from '../types';

export interface SyncState {
  lastSync: number;
  pendingChanges: any[];
  isOnline: boolean;
}

class SyncService {
  private syncState: SyncState = {
    lastSync: 0,
    pendingChanges: [],
    isOnline: navigator.onLine
  };

  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;

  constructor() {
    this.init();
  }

  private init() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.syncState.isOnline = true;
      this.scheduleSync();
    });

    window.addEventListener('offline', () => {
      this.syncState.isOnline = false;
    });

    // Load sync state from IndexedDB
    this.loadSyncState();

    // Schedule periodic sync
    this.scheduleSync();
  }

  private async loadSyncState() {
    try {
      const state = await indexedDBService.getItem('sync_state');
      if (state) {
        this.syncState = { ...this.syncState, ...state };
      }
    } catch (error) {
      console.error('Failed to load sync state:', error);
    }
  }

  private async saveSyncState() {
    try {
      await indexedDBService.setItem('sync_state', this.syncState);
    } catch (error) {
      console.error('Failed to save sync state:', error);
    }
  }

  private scheduleSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Sync every 5 minutes when online
    this.syncInterval = setInterval(() => {
      if (this.syncState.isOnline && !this.isSyncing) {
        this.performSync();
      }
    }, 5 * 60 * 1000);

    // Initial sync
    if (this.syncState.isOnline) {
      this.performSync();
    }
  }

  async performSync(): Promise<void> {
    if (this.isSyncing || !this.syncState.isOnline) {
      return;
    }

    this.isSyncing = true;

    try {
      console.log('Starting data synchronization...');

      // Sync chats
      await this.syncChats();

      // Sync knowledge
      // await this.syncKnowledge();

      // Sync user settings
      await this.syncSettings();

      // Update last sync time
      this.syncState.lastSync = Date.now();
      await this.saveSyncState();

      console.log('Data synchronization completed');

      // Notify UI of sync completion
      window.dispatchEvent(new CustomEvent('syncCompleted'));

    } catch (error) {
      console.error('Sync failed:', error);
      window.dispatchEvent(new CustomEvent('syncFailed', { detail: error }));
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncChats(): Promise<void> {
    try {
      // Get local chats
      const localChats = await indexedDBService.getAllChats();

      // Get server chats
      const serverChats = await getChats();

      // Find new/changed chats to upload
      const chatsToUpload = localChats.filter((localChat: any) => {
        const serverChat = serverChats.find((sc: any) => sc.id === localChat.id);
        return !serverChat || serverChat.timestamp < localChat.updatedAt;
      });

      // Upload changes
      for (const chat of chatsToUpload) {
        try {
          if (serverChats.find(sc => sc.id === chat.id)) {
            await updateChat(chat.id, {
              title: chat.title,
              messages: chat.messages as any
            });
          } else {
            await createChat({ title: chat.title });
            // Note: In a full implementation, we'd sync messages too
          }
        } catch (error) {
          console.error(`Failed to sync chat ${chat.id}:`, error);
        }
      }

      // Download new chats from server
      const newServerChats = serverChats.filter((serverChat: any) => {
        return !localChats.find((lc: any) => lc.id === serverChat.id);
      });

      for (const chat of newServerChats) {
        try {
          await indexedDBService.saveChat(chat);
        } catch (error) {
          console.error(`Failed to save chat ${chat.id} locally:`, error);
        }
      }

    } catch (error) {
      console.error('Chat sync failed:', error);
    }
  }

  // private async syncKnowledge(): Promise<void> {
  //   try {
  //     // Get local knowledge
  //     const localKnowledge = await indexedDBService.getAllKnowledge();

  //     // Get server knowledge
  //     const serverKnowledge = await getKnowledge();

  //     // Similar sync logic as chats
  //     const knowledgeToUpload = localKnowledge.filter(local => {
  //       const server = serverKnowledge.find(sk => sk.id === local.id);
  //       return !server || server.updated_at < local.updated_at;
  //     });

  //     for (const knowledge of knowledgeToUpload) {
  //       try {
  //         if (serverKnowledge.find(sk => sk.id === knowledge.id)) {
  //           await updateKnowledge(knowledge.id, knowledge);
  //         } else {
  //           await createKnowledge(knowledge);
  //         }
  //       } catch (error) {
  //         console.error(`Failed to sync knowledge ${knowledge.id}:`, error);
  //       }
  //     }

  //     const newServerKnowledge = serverKnowledge.filter(server => {
  //       return !localKnowledge.find(lk => lk.id === server.id);
  //     });

  //     for (const knowledge of newServerKnowledge) {
  //       try {
  //         await indexedDBService.saveKnowledge(knowledge);
  //       } catch (error) {
  //         console.error(`Failed to save knowledge ${knowledge.id} locally:`, error);
  //       }
  //     }

  //   } catch (error) {
  //     console.error('Knowledge sync failed:', error);
  //   }
  // }

  private async syncSettings(): Promise<void> {
    try {
      const localSettings = await indexedDBService.getItem('user_settings');
      const serverSettings = await getUserSettings();

      if (localSettings && serverSettings) {
        const localTime = localSettings.updated_at || 0;
        const serverTime = serverSettings.updated_at || 0;

        if (localTime > serverTime) {
          // Upload local settings
          await updateUserSettings(localSettings);
        } else if (serverTime > localTime) {
          // Download server settings
          await indexedDBService.setItem('user_settings', serverSettings);
        }
      }
    } catch (error) {
      console.error('Settings sync failed:', error);
    }
  }

  // Manual sync trigger
  async syncNow(): Promise<void> {
    return this.performSync();
  }

  // Get sync status
  getSyncStatus() {
    return {
      ...this.syncState,
      isSyncing: this.isSyncing
    };
  }

  // Add change to pending queue (for offline scenarios)
  addPendingChange(change: any) {
    this.syncState.pendingChanges.push({
      ...change,
      timestamp: Date.now()
    });
    this.saveSyncState();
  }

  // Process pending changes when back online
  private async processPendingChanges() {
    if (!this.syncState.isOnline || this.syncState.pendingChanges.length === 0) {
      return;
    }

    for (const change of this.syncState.pendingChanges) {
      try {
        switch (change.type) {
          case 'chat_update':
            await updateChat(change.chatId, change.data as any);
            break;
          // case 'knowledge_update':
          //   await updateKnowledge(change.knowledgeId, change.data);
          //   break;
          // Add more change types as needed
        }
      } catch (error) {
        console.error('Failed to process pending change:', error);
        // Keep in queue for retry
        continue;
      }

      // Remove processed change
      this.syncState.pendingChanges = this.syncState.pendingChanges.filter(c => c !== change);
    }

    await this.saveSyncState();
  }
}

export { SyncService };

// Export singleton instance
const syncService = new SyncService();
export default syncService;